import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

export const DutyParticipants = ({ onChange }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  const load = () => api.get("/duty/participants").then((r) => setRows(r.data.all_consultants || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (p) => {
    try {
      await api.post(`/duty/participants/${p.id}`, { opt_in: !p.opt_in });
      setRows((rs) => rs.map((r) => (r.id === p.id ? { ...r, opt_in: !p.opt_in } : r)));
      onChange?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const shown = rows.filter((r) => (r.name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="border-t border-[#161616] pt-4 mt-2" data-testid="duty-participants">
      <p className="text-[11px] text-[#737373] mb-3">
        Marca quem participa na escala. Cada consultor também pode ativar ou desativar na própria página.
      </p>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Procurar consultor…"
        className="w-full bg-[#0B0B0B] border border-[#1F1F1F] text-sm px-3 py-2 mb-3" data-testid="duty-participants-search" />
      <div className="max-h-72 overflow-y-auto divide-y divide-[#161616]">
        {shown.map((p) => (
          <label key={p.id} className="flex items-center gap-3 py-2 text-sm cursor-pointer" data-testid={`duty-participant-${p.id}`}>
            <input type="checkbox" checked={!!p.opt_in} onChange={() => toggle(p)} className="accent-[#E50000]" />
            <span className="flex-1 truncate">{p.name}</span>
            <span className="text-[10px] uppercase tracking-widest text-[#555]">{p.mbti || ""}</span>
          </label>
        ))}
        {shown.length === 0 && <div className="py-3 text-sm text-[#737373]">Sem resultados.</div>}
      </div>
    </div>
  );
};

const DAY_LABELS = ["S", "T", "Q", "Q", "S", "S", "D"];
const DAY_TITLES = ["segunda", "terça", "quarta", "quinta", "sexta", "sábado", "domingo"];

const DayPicker = ({ days, onToggle, testid }) => (
  <div className="flex gap-1" data-testid={testid}>
    {DAY_LABELS.map((l, i) => (
      <button key={i} type="button" title={DAY_TITLES[i]} onClick={() => onToggle(i)}
        className={`w-6 h-6 text-[10px] font-bold border ${days.includes(i) ? "bg-[#E50000] border-[#E50000] text-white" : "border-[#1F1F1F] text-[#555]"} ${i > 4 ? "rounded-full" : ""}`}
        data-testid={`${testid}-${i}`}>{l}</button>
    ))}
  </div>
);

export const DutyShifts = ({ data, onChange }) => {
  const [shifts, setShifts] = useState(data?.shifts || []);
  const [novo, setNovo] = useState({ name: "", start: "09:30", end: "13:30", days: [5, 6] });

  useEffect(() => setShifts(data?.shifts || []), [data]);

  const save = async (s) => {
    try { await api.patch(`/duty/shifts/${s.id}`, { name: s.name, start: s.start, end: s.end, days: s.days }); toast.success("Turno guardado"); onChange?.(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const add = async () => {
    if (!novo.name.trim()) return toast.error("Dá um nome ao turno");
    try { await api.post("/duty/shifts", novo); setNovo({ name: "", start: "09:30", end: "13:30", days: [5, 6] }); toast.success("Turno criado"); onChange?.(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remover o turno "${s.name}"?`)) return;
    try { await api.delete(`/duty/shifts/${s.id}`); onChange?.(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const flag = async (key, value) => {
    try { await api.patch("/duty/notifications", { [key]: value }); toast.success("Guardado"); onChange?.(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="border-t border-[#161616] pt-4 mt-2 space-y-5" data-testid="duty-shifts">
      <div>
        <div className="kw-overline mb-2">Turnos (só administradores)</div>
        <p className="text-[11px] text-[#737373] mb-2">Nos quadradinhos escolhes em que dias acontece cada turno — sábado e domingo são os dois últimos (redondos).</p>
        {shifts.map((s, i0) => (
          <div key={s.id} className="flex flex-wrap items-center gap-2 py-2 border-b border-[#161616]" data-testid={`duty-shift-${s.id}`}>
            <input value={s.name} onChange={(e) => setShifts((xs) => xs.map((x, j) => (j === i0 ? { ...x, name: e.target.value } : x)))}
              className="flex-1 min-w-[120px] bg-[#0B0B0B] border border-[#1F1F1F] text-sm px-2 py-1" />
            <input type="time" value={s.start} onChange={(e) => setShifts((xs) => xs.map((x, j) => (j === i0 ? { ...x, start: e.target.value } : x)))}
              className="bg-[#0B0B0B] border border-[#1F1F1F] text-sm px-2 py-1" />
            <input type="time" value={s.end} onChange={(e) => setShifts((xs) => xs.map((x, j) => (j === i0 ? { ...x, end: e.target.value } : x)))}
              className="bg-[#0B0B0B] border border-[#1F1F1F] text-sm px-2 py-1" />
            <DayPicker days={s.days || []} testid={`duty-shift-days-${s.id}`}
              onToggle={(i) => setShifts((xs) => xs.map((x, j) => (j === i0 ? { ...x, days: (x.days || []).includes(i) ? x.days.filter((d) => d !== i) : [...(x.days || []), i].sort() } : x)))} />
            <button onClick={() => save(s)} className="kw-btn-primary !py-1 !px-3 text-[10px]" data-testid={`duty-shift-save-${s.id}`}>Guardar</button>
            <button onClick={() => remove(s)} className="kw-btn-secondary !py-1 !px-2 text-[10px]" data-testid={`duty-shift-remove-${s.id}`}><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2 pt-3">
          <input value={novo.name} onChange={(e) => setNovo({ ...novo, name: e.target.value })} placeholder="Novo turno"
            className="flex-1 min-w-[120px] bg-[#0B0B0B] border border-[#1F1F1F] text-sm px-2 py-1" data-testid="duty-shift-new-name" />
          <input type="time" value={novo.start} onChange={(e) => setNovo({ ...novo, start: e.target.value })} className="bg-[#0B0B0B] border border-[#1F1F1F] text-sm px-2 py-1" />
          <input type="time" value={novo.end} onChange={(e) => setNovo({ ...novo, end: e.target.value })} className="bg-[#0B0B0B] border border-[#1F1F1F] text-sm px-2 py-1" />
          <DayPicker days={novo.days} testid="duty-shift-new-days"
            onToggle={(i) => setNovo((n) => ({ ...n, days: n.days.includes(i) ? n.days.filter((d) => d !== i) : [...n.days, i].sort() }))} />
          <button onClick={add} className="kw-btn-secondary !py-1 !px-3 text-[10px] inline-flex items-center gap-1" data-testid="duty-shift-add">
            <Plus className="w-3 h-3" /> Juntar turno
          </button>
        </div>
      </div>

      <div>
        <div className="kw-overline mb-2">Avisos por email</div>
        <label className="flex items-center gap-3 py-2 text-sm cursor-pointer" data-testid="duty-flag-publish">
          <input type="checkbox" checked={!!data?.notify_publish} onChange={(e) => flag("notify_publish", e.target.checked)} className="accent-[#E50000]" />
          <span>Avisar cada inscrito quando a escala do mês é publicada</span>
        </label>
        <label className="flex items-center gap-3 py-2 text-sm cursor-pointer" data-testid="duty-flag-daybefore">
          <input type="checkbox" checked={!!data?.notify_day_before} onChange={(e) => flag("notify_day_before", e.target.checked)} className="accent-[#E50000]" />
          <span>Lembrete na véspera a quem está de serviço no dia seguinte</span>
        </label>
        <label className="flex items-center gap-3 py-2 text-sm cursor-pointer" data-testid="duty-flag-swaps">
          <input type="checkbox" checked={data?.swaps_enabled !== false} onChange={(e) => flag("swaps_enabled", e.target.checked)} className="accent-[#E50000]" />
          <span>Permitir pedidos de troca entre colegas</span>
        </label>
        <p className="text-[11px] text-[#737373] mt-1">Com os avisos desligados nada sai por email — podes sempre usar «Enviar avisos agora».</p>
      </div>
    </div>
  );
};
